import { useEffect, useRef, useState } from "react";

/**
 * Global chart interactivity layer.
 *
 * Mounted once at the root. It delegates pointer events for every inline SVG
 * chart rendered inside `.article-body figure` (existing and future articles),
 * highlights the hovered slice/bar/point and floats a single shared tooltip
 * near the cursor. Purely presentational — no article markup is modified.
 */

type Tip = { x: number; y: number; label: string; value: string };

const DATA_TAGS = new Set(["path", "rect", "circle", "ellipse", "polygon", "polyline", "g", "text"]);

/** Reads the data label for a hovered element from its <title>/aria-label. */
function readLabel(el: Element): string | null {
  const own = el.querySelector(":scope > title");
  if (own?.textContent?.trim()) return own.textContent.trim();
  const aria = el.getAttribute("aria-label") ?? el.getAttribute("data-label");
  if (aria?.trim()) return aria.trim();
  const group = el.closest("g[aria-label], g");
  const gTitle = group?.querySelector(":scope > title");
  if (gTitle?.textContent?.trim()) return gTitle.textContent.trim();
  const gAria = group?.getAttribute("aria-label");
  return gAria?.trim() || null;
}

/** Splits "Category: 42%" into a name + value pair for the tooltip. */
function splitLabel(text: string): { label: string; value: string } {
  const m = text.match(/^(.*?)[:\u2014-]\s*([^:]*[\d][^:]*)$/);
  if (m?.[1] && m[2]) return { label: m[1].trim(), value: m[2].trim() };
  return { label: text, value: "" };
}

export function ChartInteractivity() {
  const [tip, setTip] = useState<Tip | null>(null);
  const activeRef = useRef<Element | null>(null);

  useEffect(() => {
    const clear = () => {
      if (activeRef.current) {
        activeRef.current.classList.remove("chart-slice-active");
        activeRef.current = null;
      }
      setTip(null);
    };

    let lastMove = 0;
    const onMove = (event: PointerEvent) => {
      // Throttle pointermove to ~30fps to reduce main thread pressure on mobile
      const now = Date.now();
      if (now - lastMove < 32 && event.type === 'pointermove') return;
      lastMove = now;

      window.requestAnimationFrame(() => {
      const target = event.target as Element | null;
      if (!target || !(target instanceof Element)) return clear();

      const svg = target.closest(".article-body svg");
      if (!svg) return clear();

      const tag = target.tagName.toLowerCase();
      if (!DATA_TAGS.has(tag)) return clear();

      const raw = readLabel(target);
      if (!raw) {
        if (activeRef.current !== target) {
          activeRef.current?.classList.remove("chart-slice-active");
          target.classList.add("chart-slice-active");
          activeRef.current = target;
        }
        return setTip(null);
      }

      if (activeRef.current !== target) {
        activeRef.current?.classList.remove("chart-slice-active");
        target.classList.add("chart-slice-active");
        activeRef.current = target;
      }

      const { label, value } = splitLabel(raw);
      setTip({ x: event.clientX, y: event.clientY, label, value });
      });
    };

    document.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerdown", onMove, { passive: true });
    document.addEventListener("pointercancel", clear, { passive: true });
    window.addEventListener("scroll", clear, { passive: true });

    return () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerdown", onMove);
      document.removeEventListener("pointercancel", clear);
      window.removeEventListener("scroll", clear);
    };
  }, []);

  if (!tip) return null;

  // Keep the tooltip on-screen on narrow/mobile viewports.
  const width = typeof window === "undefined" ? 1024 : window.innerWidth;
  const flip = tip.x > width - 200;
  const left = Math.min(Math.max(12, tip.x + (flip ? -16 : 16)), width - 12);

  return (
    <div
      role="tooltip"
      className="chart-tooltip"
      style={{
        left: `${left}px`,
        top: `${tip.y}px`,
        transform: `translate(${flip ? "-100%" : "0"}, -50%)`,
      }}
    >
      <span className="chart-tooltip-label">{tip.label}</span>
      {tip.value ? <span className="chart-tooltip-value">{tip.value}</span> : null}
    </div>
  );
}
